// 대화 한 번의 상태 규칙 — 순수 함수 (MVP API 의 대화 중계 계약, api/internal/httpapi/llm.go 머리 주석)
//   POST /api/llm/chat → 200 SSE `delta {text}` … `done` 또는 `error {code}`
//   스트림이 열리기 전의 실패는 JSON `{error}` + 상태: 401 로그인 · 402 credits_exhausted(이용권 0장) · 400/403/413 요청 · 502/503 플랫폼

export interface ChatMessage {
  readonly role: 'system' | 'user' | 'assistant';
  readonly content: string;
}

/** 중계가 내보내는 이벤트 (어댑터가 경계에서 파싱한 뒤의 모양) */
export type StreamEvent =
  | { readonly type: 'delta'; readonly text: string }
  | { readonly type: 'done' }
  | { readonly type: 'error'; readonly code: string };

export const CHAT_ERRORS = ['login_required', 'credits_exhausted', 'bad_request', 'unavailable', 'blocked', 'interrupted', 'failed'] as const;

export type ChatError = (typeof CHAT_ERRORS)[number];

export interface ChatState {
  readonly status: 'idle' | 'streaming' | 'done' | 'failed';
  /** 지금까지 받은 답. 실패해도 받은 만큼은 남긴다 */
  readonly text: string;
  readonly error: ChatError | null;
}

export const IDLE: ChatState = { status: 'idle', text: '', error: null };

export const STREAMING: ChatState = { status: 'streaming', text: '', error: null };

/** 스트림이 열리기 전에 거절됐다 — 받은 것이 없다 */
export class ChatRequestError extends Error {
  constructor(readonly kind: ChatError) {
    super(`chat request rejected: ${kind}`);
  }
}

/** 스트림이 열리기 전의 HTTP 상태·error 코드를 화면이 구분할 사유로 줄인다 */
export function toChatError(status: number, code: unknown): ChatError {
  if (status === 401) return 'login_required';
  if (status === 402 && code === 'credits_exhausted') return 'credits_exhausted';
  if (status === 400 || status === 403 || status === 413) return 'bad_request';
  if (status === 502 || status === 503) return 'unavailable';
  return 'failed';
}

/** 스트림 안의 error 이벤트 코드(플랫폼 README 「LLM 창구」)를 사유로 줄인다 */
export function fromStreamCode(code: string): ChatError {
  return code === 'llm_content_blocked' ? 'blocked' : 'unavailable';
}

/**
 * 실패 사유 → 화면 문구 키 (`error.<사유>`). 이용권 소진 안내만 앱의 광고 보상 사용 여부로 가른다 —
 * 광고가 꺼진 앱(생성 MVP 기본)에서 「광고를 보면」은 틀린 안내다 (prd/123 Discovery)
 */
export function chatErrorMessageKey(error: ChatError, adRewardOn: boolean): string {
  return error === 'credits_exhausted' && adRewardOn ? 'error.credits_exhausted_ad' : `error.${error}`;
}

export function reduce(state: ChatState, event: StreamEvent): ChatState {
  if (state.status !== 'streaming') return state; // 끝난 뒤 늦게 온 이벤트는 버린다
  switch (event.type) {
    case 'delta':
      return { ...state, text: state.text + event.text };
    case 'done':
      return { ...state, status: 'done' };
    case 'error':
      // 조각을 한 번이라도 받은 뒤의 실패는 서버가 이용권을 돌려주지 않는다 — 「이용권은 그대로」로 안내하지 않도록 따로 가른다
      return { ...state, status: 'failed', error: state.text === '' ? fromStreamCode(event.code) : 'interrupted' };
  }
}

/** done·error 없이 스트림이 끝났다 — 연결이 끊긴 것이다. 이미 끝난 상태는 그대로 */
export function interrupted(state: ChatState): ChatState {
  return state.status === 'streaming' ? { ...state, status: 'failed', error: 'interrupted' } : state;
}

export function failed(error: ChatError, text = ''): ChatState {
  return { status: 'failed', text, error };
}
